import {useAtom, useAtomValue} from 'jotai';
import {isEqual} from 'lodash';
import {useEffect, useState} from 'react';
import {useQueryMe, useQueryMeObserver} from '@/api/session';
import {userAtom} from '@/atom';
import {DEVICE_ID_KEY} from '@/constants';
import {TrialInfoType} from '@/enums';
import {COUPON_CODES, SMART_STORE_26_CODE} from '@/constants/payments';
import {setGtagUserInfomation} from '@/utils';
import {useQueryClient} from '@tanstack/react-query';
import {useUplusUserAgent} from '../userAent';

export const useDeviceId = () => {
  const [deviceId, setDeviceId] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(DEVICE_ID_KEY);

    if (stored) {
      setDeviceId(stored);
    }
  }, []);

  const generateDeviceId = () => {
    const existing = localStorage.getItem(DEVICE_ID_KEY);

    if (existing) {
      setDeviceId(existing);
      return existing;
    }

    const uuid = crypto.randomUUID();

    localStorage.setItem(DEVICE_ID_KEY, uuid);
    setDeviceId(uuid);
    return uuid;
  };

  const getDeviceId = () => {
    return deviceId;
  };

  return {deviceId, getDeviceId, generateDeviceId};
};

// 앱 최상위 컴포넌트에서 한 번만 참조할 것.
export const useSessionObserver = () => {
  const queryClient = useQueryClient();
  const [user, setUser] = useAtom(userAtom);
  const result = useQueryMe(!user?.id);
  const {isError, data} = result;
  const isUplusApp = useUplusUserAgent();
  const {deviceId} = useDeviceId();

  useEffect(() => {
    if (isError) {
      setUser(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  useEffect(() => {
    // 로그인 정보 변경 시 지도 데이터 캐싱 초기화 (유저 연동 데이터인 경우)
    queryClient.removeQueries({queryKey: ['map']});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    // 유플러스앱 SSO는 모든 계정이 공통 이메일을 사용하기 때문에 이메일로 GA 식별자를 설정하지 않음.
    if (isUplusApp === null || isUplusApp) {
      return;
    }

    if (user?.email) {
      // set GA user info
      setGtagUserInfomation({
        user_id: user.email,
      });
    }
  }, [user?.email, isUplusApp]);

  useEffect(() => {
    // 유플러스앱인 경우 이메일 대신 디바이스 아이디로 GA 식별자를 설정.
    if (isUplusApp && deviceId) {
      setGtagUserInfomation({
        user_id: deviceId,
      });
    }
  }, [isUplusApp, deviceId]);

  useEffect(() => {
    if (data?.result) {
      const nextMyData = data.result;

      setUser((prev) => {
        if (!isEqual(prev, nextMyData)) {
          return nextMyData?.id ? nextMyData : undefined;
        } else {
          return prev;
        }
      });
    } else {
      setUser(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.result]);

  return {
    ...result,
  };
};

export const useSession = () => {
  const user = useAtomValue(userAtom);
  const hasSession = !!user?.id;
  const {isPending, data, ...observer} = useQueryMeObserver();
  // 지도 오픈 사전신청자 이용권 이용 중인 경우
  const isOpenPreRegistrationUser = !!user?.trialInfo?.find(
    (i) =>
      i.type === TrialInfoType.preRegistration &&
      !COUPON_CODES.includes(i.code) && // 사전신청 이후 발급된 쿠폰 사용자가 아니고
      !!i.useEndedAt &&
      !i.terminatedAt,
  );

  // 스마트상점26 이용권 이용 중인 경우
  const isSmartStoreActiveUser = !!user?.trialInfo?.find(
    (i) => i.code === SMART_STORE_26_CODE && !!i.useEndedAt && !i.terminatedAt,
  );

  return {
    hasSession,
    user,
    isOpenPreRegistrationUser,
    isSmartStoreActiveUser,
    isPending: isPending || (!!data?.result?.id && !user?.id),
    isPaidUser: !!user && !!user.tier && !!user.useBeganAt && !!user.useEndedAt,
    ...observer,
  };
};
